import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MatiereProfileLayout from './MatiereProfileLayout';

// import utility file
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';

import MatiereService from '../../services/MatiereService';

const ViewMatiere = () => {
	const { id } = useParams();
	const [matiere, setMatiere] = useState(null);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [selectedSection, setSelectedSection] = useState('');

	const SECTION_CHOICES = [
		{ value: 'en', label: 'Angolophone' },
		{ value: 'fr', label: 'Francophone' }
	];

	useEffect(() => {
		Promise.all([MatiereService.getMatiere(id)]).then(([matiereData]) => {
			setMatiere(matiereData.data);

			// Set default selection for section
			if (matiereData && matiereData.data) {
				const matiereSection = matiereData.data.section;
				setSelectedSection(matiereSection);
			}
		});
	}, [id]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const matiereData = new FormData();

				// Add all your form data to the FormData
				matiereData.append('nom', form.nom.value);
				matiereData.append('section', form.section.value);
				if (selectedSection === 'fr') {
					matiereData.append('points_16', form.points_16.value);
					matiereData.append('points_17', form.points_17.value);
					matiereData.append('points_18', form.points_18.value);
				} else if (selectedSection === 'en') {
					matiereData.append('points_10', form.points_10.value);
					matiereData.append('points_11', form.points_11.value);
					matiereData.append('points_12', form.points_12.value);
				}
				matiereData.append('annee_scolaire', matiere.annee_scolaire);

				const response = await MatiereService.updateMatiere(id, matiereData);

				// Reset the form and state variables after successful update
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Add this line to refresh the page
				window.location.reload();
			} catch (error) {
				console.error(
					'Erreur lors de la mise à jour des informations de la matière',
					error
				);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<MatiereProfileLayout>
			{matiere && (
				<>
					<Card className="mb-3 ">
						{/* Card body */}
						<Card.Body>
							{/*  Form  */}
							<Form
								className="mb-3"
								noValidate
								validated={validated}
								onSubmit={handleSubmit}
							>
								{showAlert && (
									<Alert variant="danger">
										<ExclamationTriangleFill size={25} className="me-1" />
										{alertMessage}
									</Alert>
								)}

								<Row>
									<Col xs={12} className="mb-3">
										{/* Nom */}
										<Form.Group>
											<Row>
												<Col sm={12} className="mb-3">
													<Form.Label htmlFor="nom">
														Nom <span className="text-danger">*</span>
													</Form.Label>
													<Form.Control
														name="nom"
														type="text"
														placeholder="Saisir le nom du Matiere"
														id="nom"
														required
														isInvalid={!!feedbackMessages['nom']}
														onChange={(e) => {
															setFeedbackMessages((prevMessages) => ({
																...prevMessages,
																nom: e.target.value ? '' : 'Le nom est requis.'
															}));
														}}
														defaultValue={matiere.nom}
													/>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['nom']}
													</Form.Control.Feedback>
												</Col>
											</Row>
										</Form.Group>
									</Col>

									<Col xs={12} className="mb-3">
										<Form.Label htmlFor="section">
											Section <span className="text-danger">*</span>
										</Form.Label>
										<Form.Select
											name="section"
											required
											value={selectedSection}
											isInvalid={!!feedbackMessages['section']}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedSection(value);
												// Reset feedback messages for points fields when section changes
												setFeedbackMessages({});
											}}
										>
											<option value="">Sélectionner la section</option>
											{SECTION_CHOICES.map((choice, index) => (
												<option key={index} value={choice.value}>
													{choice.label}
												</option>
											))}
										</Form.Select>
										<Form.Control.Feedback type="invalid">
											{feedbackMessages['section']}
										</Form.Control.Feedback>
									</Col>

									{/* Affichage des points pour les classes Francophones */}
									{selectedSection === 'fr' && (
										<Col xs={12} className="mb-3">
											<Form.Group>
												<Row>
													<Col xs={12} md={4} className="mb-3">
														<Form.Label htmlFor="points_16">
															Points CE2 <span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															name="points_16"
															type="number"
															placeholder="Points CE2"
															id="points_16"
															required
															isInvalid={!!feedbackMessages['points_16']}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_16: e.target.value
																		? ''
																		: 'Les points sont requis.'
																}));
															}}
															defaultValue={matiere.points_16}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_16']}
														</Form.Control.Feedback>
													</Col>

													<Col xs={12} md={4} className="mb-3">
														<Form.Label htmlFor="points_17">
															Points CM1 <span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															name="points_17"
															type="number"
															placeholder="Points CM1"
															id="points_17"
															required
															isInvalid={!!feedbackMessages['points_17']}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_17: e.target.value
																		? ''
																		: 'Les points sont requis.'
																}));
															}}
															defaultValue={matiere.points_17}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_17']}
														</Form.Control.Feedback>
													</Col>

													<Col xs={12} md={4} className="mb-3">
														<Form.Label htmlFor="points_18">
															Points CM2 <span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															name="points_18"
															type="number"
															placeholder="Points CM2"
															id="points_18"
															required
															isInvalid={!!feedbackMessages['points_18']}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_18: e.target.value
																		? ''
																		: 'Les points sont requis.'
																}));
															}}
															defaultValue={matiere.points_18}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_18']}
														</Form.Control.Feedback>
													</Col>
												</Row>
											</Form.Group>
										</Col>
									)}

									{/* Affichage des points pour les classes Anglophones */}
									{selectedSection === 'en' && (
										<Col xs={12} className="mb-3">
											<Form.Group>
												<Row>
													<Col xs={12} md={4} className="mb-3">
														<Form.Label htmlFor="points_10">
															Points Class Four
															<span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															name="points_10"
															type="number"
															placeholder="Points Class Four"
															id="points_10"
															required
															isInvalid={!!feedbackMessages['points_10']}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_10: e.target.value
																		? ''
																		: 'Les points sont requis.'
																}));
															}}
															defaultValue={matiere.points_10}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_10']}
														</Form.Control.Feedback>
													</Col>

													<Col xs={12} md={4} className="mb-3">
														<Form.Label htmlFor="points_11">
															Points Class Five{' '}
															<span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															name="points_11"
															type="number"
															placeholder="Points Class Five"
															id="points_11"
															required
															isInvalid={!!feedbackMessages['points_11']}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_11: e.target.value
																		? ''
																		: 'Les points sont requis.'
																}));
															}}
															defaultValue={matiere.points_11}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_11']}
														</Form.Control.Feedback>
													</Col>

													<Col xs={12} md={4} className="mb-3">
														<Form.Label htmlFor="points_12">
															Points Class Six
															<span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															name="points_12"
															type="number"
															placeholder="Points Class Six"
															id="points_12"
															required
															isInvalid={!!feedbackMessages['points_12']}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_12: e.target.value
																		? ''
																		: 'Les points sont requis.'
																}));
															}}
															defaultValue={matiere.points_12}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_12']}
														</Form.Control.Feedback>
													</Col>
												</Row>
											</Form.Group>
										</Col>
									)}

									{/* Buttons */}
									<Col xs={12} className="mt-4">
										<Button variant="success" type="submit">
											Mettre à jour
										</Button>
										<Button
											type="reset"
											variant="outline-danger"
											className="ms-2"
										>
											Effacer
										</Button>
									</Col>
								</Row>
							</Form>
						</Card.Body>
					</Card>
				</>
			)}
		</MatiereProfileLayout>
	);
};

export default ViewMatiere;
